import React, { useState, useEffect } from 'react';
import GameLobby from './components/GameLobby';
import GameBoard from './components/GameBoard';
import WaitingRoom from './components/WaitingRoom';
import NameInput from './components/NameInput';
import { useWebSocket } from './hooks/useWebSocket';
import { WS_URL } from './config';
import './App.css';

function App() {
  const [gameState, setGameState] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [playerName, setPlayerName] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'create' or 'join'
  const [showNameInput, setShowNameInput] = useState(false);

  // Generate a unique player ID on component mount (only once)
  useEffect(() => {
    if (!playerId) {
      const newPlayerId = `player_${Math.random().toString(36).substr(2, 9)}`;
      setPlayerId(newPlayerId);
    }
  }, []); // Empty dependency array - only run once

  // Use a ref to track the current WebSocket URL
  const [currentWsUrl, setCurrentWsUrl] = useState(null);
  
  // Set WebSocket URL only after both playerName and gameId are set
  useEffect(() => {
    if (playerName && gameId && playerId) {
      const wsUrl = `${WS_URL}/ws/${gameId}/${playerId}`;
      setCurrentWsUrl(wsUrl);
    }
  }, [playerName, gameId, playerId]);
  
  const { sendMessage, lastMessage, connectionStatus } = useWebSocket(currentWsUrl);

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      const message = JSON.parse(lastMessage.data);
      // Log all incoming WebSocket messages for debugging
      console.log('[WebSocket] Received message:', message);
      
      switch (message.type) {
        case 'connected':
          console.log('WebSocket connected, pending action:', pendingAction);
          setIsConnected(true);
          setError(null);
          // Send pending action once connected
          if (pendingAction === 'create') {
            console.log('Sending create_game message');
            sendMessage({ type: 'create_game', player_name: playerName });
            setPendingAction(null);
          } else if (pendingAction === 'join') {
            console.log('Sending join_game message');
            sendMessage({ type: 'join_game', player_name: playerName });
            setPendingAction(null);
          }
          break;
          
        case 'game_created':
          setGameState(message.state);
          setError(null);
          break;
          
        case 'game_started':
          setGameState(message.state);
          setError(null);
          break;
          
        case 'cards_played':
        case 'player_drew':
        case 'card_drawn':
        case 'last_card_called':
        case 'suit_selected':
          setGameState(message.state);
          setError(null);
          break;
          
        case 'error':
          // Provide more helpful error messages
          let errorMessage = message.message;
          if (message.message === 'Game not found') {
            errorMessage = 'Game not found. Please check the game code and try again. If the problem persists, the game may have expired or the server may be experiencing issues.';
          }
          setError(errorMessage);
          break;
          
        default:
          console.log('Unhandled message type:', message.type);
      }
    }
  }, [lastMessage, pendingAction, sendMessage]);

  const createGame = () => {
    setShowNameInput(true);
    setPendingAction('create');
  };

  const joinGame = (gameIdToJoin) => {
    setShowNameInput(true);
    setPendingAction('join');
    setGameId(gameIdToJoin);
  };

  const handleNameSubmit = (name) => {
    setPlayerName(name);
    setShowNameInput(false);
    if (pendingAction === 'create') {
      // For create, generate gameId after name is entered
      const newGameId = `game_${Math.random().toString(36).substr(2, 9)}`;
      setGameId(newGameId);
    }
    // For join, gameId is already set
  };



  const playCards = (cards) => {
    sendMessage({ 
      type: 'play_card', 
      game_id: gameId,
      player_id: playerId,
      cards: cards.map(card => ({
        suit: card.suit,
        rank: card.rank,
        value: card.value
      }))
    });
  };

  const drawCard = () => {
    sendMessage({ type: 'draw_card' });
  };

  const callLastCard = () => {
    sendMessage({ type: 'call_last_card' });
  };

  const selectSuit = (suit) => {
    sendMessage({ type: 'select_suit', suit });
  };

  // Show name input if player name not set
  if (showNameInput) {
    return (
      <NameInput
        onSubmit={handleNameSubmit}
        onBack={() => {
          setShowNameInput(false);
          setPendingAction(null);
          setGameId(null);
        }}
        action={pendingAction}
      />
    );
  }

  // Show loading state while connecting
  if (gameId && !isConnected) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Connecting to game...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-600 text-white p-4 rounded-lg mb-4">
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p>{error}</p>
          </div>
          <button 
            onClick={() => {
              setError(null);
              setGameState(null);
              setGameId(null);
              setPendingAction(null);
            }}
            className="btn btn-primary"
          >
            Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  // Show waiting room if game is waiting for second player
  if (gameState && gameState.phase === 'waiting') {
    return (
      <WaitingRoom
        gameId={gameId}
        onBackToLobby={() => {
          setGameState(null);
          setGameId(null);
          setPendingAction(null);
        }}
        connectionStatus={connectionStatus}
        playerName={playerName}
      />
    );
  }

  // Show game board if game is active
  if (gameState && gameState.phase !== 'waiting') {
    return (
      <GameBoard
        gameState={gameState}
        playerId={playerId}
        onPlayCards={playCards}
        onDrawCard={drawCard}
        onCallLastCard={callLastCard}
        onSelectSuit={selectSuit}
        connectionStatus={connectionStatus}
      />
    );
  }

  // Show lobby
  return (
    <GameLobby
      onCreateGame={createGame}
      onJoinGame={joinGame}
      connectionStatus={connectionStatus}
    />
  );
}

export default App; 